import axios from 'axios';

const DEFAULT_MESSAGE = 'Failed to create service';

/**
 * Pulls the duplicate-service ids out of a 400 response body. The BFF returns
 * `{ message, duplicatedServiceIds: number[] }` when the submitted job matches
 * an existing one, so we can name the offending job(s) in the error message.
 */
const getDuplicateServiceIds = (data: unknown): number[] => {
  if (typeof data !== 'object' || data === null) return [];
  const ids = (data as { duplicatedServiceIds?: unknown }).duplicatedServiceIds;
  if (!Array.isArray(ids)) return [];
  return ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
};

const getServerMessage = (data: unknown): string | undefined => {
  if (typeof data !== 'object' || data === null) return undefined;
  const message = (data as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() ? message : undefined;
};

const buildDuplicateMessage = (ids: number[]): string => {
  if (ids.length === 1) {
    return `A matching job already exists (job #${ids[0]}). Open that job instead of creating a new one.`;
  }
  const list = ids.map((id) => `#${id}`).join(', ');
  return `Matching jobs already exist (jobs ${list}). Open one of those instead of creating a new one.`;
};

/**
 * Turns a create-service failure into a user-facing message. The generic axios
 * "Request failed with status code 400" is replaced with the BFF's reason — and
 * for the duplicate-job case (a 400 carrying `duplicatedServiceIds`) we surface
 * a friendlier message that names the existing job(s).
 */
export const getCreateServiceErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return "Couldn't reach the server. Please check your connection and try again.";
    }
    const { status, data } = error.response;
    if (status === 400) {
      const duplicateIds = getDuplicateServiceIds(data);
      if (duplicateIds.length > 0) return buildDuplicateMessage(duplicateIds);
    }
    const serverMessage = getServerMessage(data);
    if (serverMessage) return serverMessage;
  }
  if (error instanceof Error && error.message) return error.message;
  return DEFAULT_MESSAGE;
};
